import { focusKeys } from '../_lib/results'
import type { ResultRow } from '../types'

const ROW_HEIGHT = 42
const TOP = 34
const WIDTH = 700
/** Where the importance column ends and the spending column begins. */
const LEFT_X = 262
const RIGHT_X = 438

/**
 * SVG text is sized with presentation attributes rather than `text-*` utilities:
 * Tailwind's font-size classes do not apply inside SVG, which is the exception the
 * typography rule carves out.
 */
const COLOR = {
  fall: 'var(--color-neutral-darker)',
  rise: 'var(--color-gold)',
  hold: 'var(--color-smoke)',
  label: 'var(--color-gray-dark)',
  muted: 'var(--color-neutral-dark)',
  rank: 'var(--color-neutral-darker)',
  highlight: 'var(--color-green-lighter-2)',
  surface: 'var(--color-card)',
  onHighlight: 'var(--color-gray-darker)',
} as const

function strokeFor(move: number) {
  if (move > 0) return COLOR.fall
  if (move < 0) return COLOR.rise
  return COLOR.hold
}

function moveTag(move: number) {
  if (move === 0) return ''
  if (move > 0) return `  ↓${move}`
  return `  ↑${Math.abs(move)}`
}

/**
 * Slope chart: each value's importance rank on the left, its spending rank on the right,
 * joined by a curve. The biggest drop and the biggest jump are thickened and highlighted.
 */
export function ComparisonChart({ rows }: { rows: ResultRow[] }) {
  const focus = focusKeys(rows)
  const height = TOP + rows.length * ROW_HEIGHT + 16
  const y = (rank: number) => TOP + (rank - 0.5) * ROW_HEIGHT
  const midX = (LEFT_X + RIGHT_X) / 2

  const byImportance = [...rows].sort((a, b) => a.imp - b.imp)
  const bySpend = [...rows].sort((a, b) => a.spend - b.spend)

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${height}`}
      className="block w-full min-w-160"
      role="img"
      aria-label="Chart connecting each value's importance rank to its spending rank."
    >
      <text x={LEFT_X} y={15} textAnchor="end" fontSize={10.5} fontWeight={700} fill={COLOR.muted}>
        WHAT VALUES MATTER
      </text>
      <text x={RIGHT_X} y={15} fontSize={10.5} fontWeight={700} fill={COLOR.muted}>
        WHERE THE MONEY WENT
      </text>

      {rows.map((r) => {
        const hot = focus.has(r.k)
        const y1 = y(r.imp)
        const y2 = y(r.spend)
        return (
          <path
            key={`link-${r.k}`}
            fill="none"
            stroke={strokeFor(r.move)}
            strokeWidth={hot ? Math.min(4.6, 2.4 + Math.abs(r.move) * 0.55) : 1.4}
            strokeOpacity={hot ? 0.9 : 0.32}
            d={`M ${LEFT_X} ${y1} C ${midX} ${y1} ${midX} ${y2} ${RIGHT_X} ${y2}`}
          />
        )
      })}

      {byImportance.map((r) => {
        const yy = y(r.imp)
        return (
          <g key={`imp-${r.k}`}>
            <text x={LEFT_X - 32} y={yy + 4} textAnchor="end" fontSize={12.5} fill={COLOR.label}>
              {r.n}
            </text>
            <circle cx={LEFT_X - 15} cy={yy} r={11} fill={COLOR.surface} stroke={COLOR.hold} />
            <text x={LEFT_X - 15} y={yy + 4} textAnchor="middle" fontSize={11} fontWeight={700} fill={COLOR.rank}>
              {r.imp}
            </text>
          </g>
        )
      })}

      {bySpend.map((r) => {
        const yy = y(r.spend)
        const hot = focus.has(r.k)
        const tag = moveTag(r.move)
        return (
          <g key={`spend-${r.k}`}>
            <circle
              cx={RIGHT_X + 15}
              cy={yy}
              r={11}
              fill={hot ? COLOR.highlight : COLOR.surface}
              stroke={hot ? COLOR.highlight : COLOR.hold}
            />
            <text
              x={RIGHT_X + 15}
              y={yy + 4}
              textAnchor="middle"
              fontSize={11}
              fontWeight={700}
              fill={hot ? COLOR.onHighlight : COLOR.rank}
            >
              {r.spend}
            </text>
            <text x={RIGHT_X + 32} y={yy + 4} fontSize={12.5} fill={COLOR.label}>
              {r.n}
              {tag && (
                <tspan fill={strokeFor(r.move)} fontWeight={hot ? 700 : 500}>
                  {tag}
                </tspan>
              )}
            </text>
          </g>
        )
      })}
    </svg>
  )
}
